import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  Mail,
  Send,
  Beaker,
  DollarSign,
  Ticket,
  FileText,
  Sparkles,
  MessageSquare,
  RotateCcw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileWarning,
  CreditCard,
} from 'lucide-react';
import type { AdminOutletContext, EmailLogEntry, QuoteRecord, Ticket as DeskTicket } from '../types';
import { EMAIL_SECTIONS_META } from '../types';
import MissingAgentNotice from '../components/MissingAgentNotice';
import { useAgent } from '../context/AgentsContext';
import {
  appendActivity,
  appendEmailLog,
  appendNotification,
  getQuoteById,
  loadCms,
  loadEmailLog,
  loadQuotes,
} from '../lib/skyluxStore';
import { fetchQuotes, fetchTickets, sendTransactionalEmail } from '../lib/api';
import { mergeQuotes } from '../lib/mergeQuotes';
import { parseQuoteIdFromTicket, ticketReceiptEmailWasSent } from '../lib/quoteTicketLink';
import {
  formatEmailLogFailureFooter,
  formatEmailLogSuccessFooter,
  resolveEmailLogDelivery,
  stripEmailLogPreviewBody,
} from '../lib/emailLogDelivery';
import { resendFromEmailLog } from '../lib/resendFromEmailLog';
import { sendTicketReceiptEmail } from '../lib/sendTicketReceiptEmail';
const SECTION_VISUAL: Record<
  EmailLogEntry['kind'],
  { accent: string; bg: string; border: string; Icon: React.ComponentType<{ size?: number; color?: string }> }
> = {
  quote: { accent: '#2563eb', bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '#93c5fd', Icon: FileText },
  oaf: { accent: '#1e293b', bg: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', border: '#94a3b8', Icon: CreditCard },
  ticket_receipt: { accent: '#059669', bg: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '#6ee7b7', Icon: Ticket },
  payment_reminder: { accent: '#d97706', bg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '#fcd34d', Icon: DollarSign },
  welcome: { accent: '#7c3aed', bg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', border: '#c4b5fd', Icon: Sparkles },
  test: { accent: '#475569', bg: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', border: '#cbd5e1', Icon: Beaker },
  ticket_update: { accent: '#4f46e5', bg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', border: '#a5b4fc', Icon: MessageSquare },
};

const KIND_LABEL: Record<EmailLogEntry['kind'], string> = {
  quote: 'Quote',
  oaf: 'OAF payment',
  ticket_receipt: 'E-ticket',
  payment_reminder: 'Payment reminder',
  welcome: 'Welcome',
  test: 'Test',
  ticket_update: 'Ticket desk',
};

function findTicketForQuote(tickets: DeskTicket[], quoteId: string): DeskTicket | undefined {
  return tickets.find((t) => parseQuoteIdFromTicket(t) === quoteId);
}

const AdminEmailCenter = () => {
  const { agentId } = useParams();
  const { globalSearch } = useOutletContext<AdminOutletContext>();
  const agent = useAgent(agentId);
  const [log, setLog] = useState(() => loadEmailLog());
  const [testTo, setTestTo] = useState(agent?.email ?? '');
  const [remQuoteId, setRemQuoteId] = useState('');
  const [resendQuoteId, setResendQuoteId] = useState('');
  const [storeTick, setStoreTick] = useState(0);
  const [paidQuotes, setPaidQuotes] = useState<QuoteRecord[]>([]);
  const [tickets, setTickets] = useState<DeskTicket[]>([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [resending, setResending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [resendingLogId, setResendingLogId] = useState<string | null>(null);

  const refreshStore = useCallback(() => {
    setLog(loadEmailLog());
    setStoreTick((t) => t + 1);
  }, []);

  useEffect(() => {
    const fn = () => refreshStore();
    window.addEventListener('skylux-storage', fn);
    return () => window.removeEventListener('skylux-storage', fn);
  }, [refreshStore]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingQuotes(true);
      try {
        const [fromApi, deskTickets] = await Promise.all([fetchQuotes(), fetchTickets()]);
        if (cancelled) return;
        const merged = mergeQuotes(fromApi, loadQuotes());
        setPaidQuotes(merged.filter((q) => q.paymentStatus === 'paid'));
        setTickets(deskTickets);
      } catch {
        if (cancelled) return;
        const local = loadQuotes().filter((q) => q.paymentStatus === 'paid');
        setPaidQuotes(local);
        setTickets([]);
      } finally {
        if (!cancelled) setLoadingQuotes(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [storeTick]);

  const unpaidQuotes = useMemo(() => {
    void storeTick;
    return loadQuotes().filter((q) => q.paymentStatus === 'unpaid');
  }, [storeTick]);

  const filtered = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return log;
    return log.filter(
      (e) =>
        e.subject.toLowerCase().includes(q) ||
        e.preview.toLowerCase().includes(q) ||
        e.to.toLowerCase().includes(q),
    );
  }, [log, globalSearch]);

  const selectedResendQuote = resendQuoteId ? paidQuotes.find((q) => q.id === resendQuoteId) : undefined;
  const resendTicket = selectedResendQuote ? findTicketForQuote(tickets, selectedResendQuote.id) : undefined;

  const findQuoteForResend = useCallback(
    (quoteId: string) => paidQuotes.find((q) => q.id === quoteId) ?? getQuoteById(quoteId),
    [paidQuotes],
  );

  if (!agent) {
    return (
      <div className="email-center-page animate-fade">
        <MissingAgentNotice />
      </div>
    );
  }

  const sendTest = async () => {
    const to = testTo.trim();
    if (!to) {
      window.alert('Enter a recipient address.');
      return;
    }
    const subject = 'Skylux deliverability test';
    const textBody = [
      'This is a deliverability test from the Skylux admin panel.',
      '',
      `Agent context: ${agent.name} (${agent.email}).`,
    ].join('\n');
    setSendingTest(true);
    try {
      const mail = await sendTransactionalEmail({ to, subject, text: textBody });
      appendEmailLog({
        id: `em_${Date.now()}`,
        at: new Date().toISOString(),
        agentId: agent.id,
        agentEmail: mail.from,
        to,
        subject,
        kind: 'test',
        preview: textBody + formatEmailLogSuccessFooter(mail.from, mail.messageId),
        deliveryStatus: 'sent',
      });
      appendNotification({
        id: `n_${Date.now()}`,
        type: 'email',
        title: 'Deliverability test',
        message: `Sent from ${mail.from} to ${to}`,
        at: new Date().toISOString(),
        read: false,
      });
      refreshStore();
      window.alert(`Test email sent from ${mail.from} to ${to}.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Email send failed';
      appendEmailLog({
        id: `em_${Date.now()}`,
        at: new Date().toISOString(),
        agentId: agent.id,
        agentEmail: agent.email,
        to,
        subject,
        kind: 'test',
        preview: textBody + formatEmailLogFailureFooter(msg),
        deliveryStatus: 'failed',
        errorMessage: msg,
      });
      refreshStore();
      window.alert(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const sendPaymentReminderFromCenter = async () => {
    const q = getQuoteById(remQuoteId);
    if (!q) {
      window.alert('Select an unpaid quote.');
      return;
    }
    const to = q.clientSnapshot.email?.trim();
    if (!to) {
      window.alert('This quote has no client email on file. Update the client first.');
      return;
    }
    setSendingReminder(true);
    const cms = loadCms();
    const preview = `${cms.paymentReminderBody}\n\nPay: ${q.paymentUrl}\nAmount: $${q.pricePerPerson}`;
    const subject = `Reminder: payment — ${q.flight.departure} to ${q.flight.arrival}`;
    let deliveryStatus: 'sent' | 'failed' = 'sent';
    let errorMessage: string | undefined;
    let fromAddr = agent.email;
    let previewFinal = preview;
    try {
      const mail = await sendTransactionalEmail({ to, subject, text: preview });
      fromAddr = mail.from;
      previewFinal = preview + formatEmailLogSuccessFooter(mail.from, mail.messageId);
    } catch (e) {
      deliveryStatus = 'failed';
      errorMessage = e instanceof Error ? e.message : 'Unknown error';
      previewFinal = preview + formatEmailLogFailureFooter(errorMessage);
    }
    appendEmailLog({
      id: `em_${Date.now()}`,
      at: new Date().toISOString(),
      agentId: agent.id,
      agentEmail: fromAddr,
      to,
      subject,
      kind: 'payment_reminder',
      preview: previewFinal,
      deliveryStatus,
      errorMessage,
      quoteId: q.id,
    });
    appendActivity({
      id: `act_${Date.now()}`,
      clientId: q.clientId,
      at: new Date().toISOString(),
      type: 'payment_reminder',
      title: 'Payment reminder (email center)',
      detail: q.id,
    });
    appendNotification({
      id: `n_${Date.now()}`,
      type: 'reminder',
      title: 'Payment reminder',
      message: `${q.clientSnapshot.name} — $${q.pricePerPerson}`,
      at: new Date().toISOString(),
      read: false,
    });
    refreshStore();
    window.alert(deliveryStatus === 'sent' ? 'Payment reminder email sent.' : `Not delivered: ${errorMessage}`);
    setSendingReminder(false);
  };

  const resendLogEntry = async (entry: EmailLogEntry) => {
    setResendingLogId(entry.id);
    try {
      const result = await resendFromEmailLog(entry, agent, {
        tickets,
        findQuote: (id) => findQuoteForResend(id) ?? loadQuotes().find((q) => q.id === id),
      });
      refreshStore();
      setStoreTick((t) => t + 1);
      window.alert(result.message);
    } finally {
      setResendingLogId(null);
    }
  };

  const resendTicketEmail = async () => {
    const quote = paidQuotes.find((q) => q.id === resendQuoteId) ?? getQuoteById(resendQuoteId);
    if (!quote) {
      window.alert('Select a paid quote.');
      return;
    }
    if (quote.paymentStatus !== 'paid') {
      window.alert('This quote is not marked as paid yet.');
      return;
    }
    const ticket = findTicketForQuote(tickets, quote.id);
    if (!ticket) {
      window.alert('No support ticket linked to this quote. Open Ticket desk or resend from the original quote flow.');
      return;
    }
    setResending(true);
    try {
      const result = await sendTicketReceiptEmail(quote, ticket, agent, { resend: true });
      if (result.ok) {
        refreshStore();
        setStoreTick((t) => t + 1);
        window.alert(result.message);
      } else {
        window.alert(result.message);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="email-center-page animate-fade">
      <header className="email-center-page__header">
        <h2>Email center</h2>
        <p>
          Client-facing flows send mail through the API using your server&apos;s SMTP settings (From address is{' '}
          <code>MAIL_FROM</code> / <code>SMTP_USER</code>). The log below is stored in this browser.
        </p>
      </header>

      <section className="email-center-types" aria-label="Email types">
        {EMAIL_SECTIONS_META.map((s) => {
          const vis = SECTION_VISUAL[s.id];
          const Icon = vis.Icon;
          return (
            <article
              key={s.id}
              className="email-center-type-card"
              style={{
                background: vis.bg,
                borderColor: vis.border,
              }}
            >
              <div className="email-center-type-card__icon" style={{ background: 'white', color: vis.accent }}>
                <Icon size={22} color={vis.accent} />
              </div>
              <h3 style={{ color: vis.accent }}>{s.label}</h3>
              <p>{s.description}</p>
            </article>
          );
        })}
      </section>

      <div className="email-center-actions">
        <section className="email-center-panel email-center-panel--accent">
          <div className="email-center-panel__head">
            <div className="email-center-panel__icon-wrap email-center-panel__icon-wrap--green">
              <Ticket size={22} />
            </div>
            <div>
              <h3>Resend e-ticket</h3>
              <p>Resend the ticket receipt (View ticket + View invoice) to the client for a paid booking.</p>
            </div>
          </div>
          {loadingQuotes ? (
            <p className="email-center-panel__muted">
              <Loader2 size={16} className="email-center-spin" style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Loading paid quotes…
            </p>
          ) : paidQuotes.length === 0 ? (
            <p className="email-center-panel__muted">No paid quotes yet. E-tickets can be resent after payment is recorded.</p>
          ) : (
            <>
              <div className="email-center-panel__fields">
                <div className="email-center-field">
                  <label htmlFor="resend-quote">Paid quote</label>
                  <select
                    id="resend-quote"
                    value={resendQuoteId}
                    onChange={(e) => setResendQuoteId(e.target.value)}
                  >
                    <option value="">Select booking…</option>
                    {paidQuotes.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.clientSnapshot.name} · {q.flight.departure}→{q.flight.arrival} · ${q.pricePerPerson}
                        {ticketReceiptEmailWasSent(q) ? ' · sent' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedResendQuote && (
                  <div className="email-center-resend-meta">
                    <span>
                      <strong>To:</strong> {selectedResendQuote.clientSnapshot.email || '—'}
                    </span>
                    <span>
                      <strong>Confirmation:</strong> {selectedResendQuote.confirmationNumber ?? '—'}
                    </span>
                    <span>
                      <strong>Desk ticket:</strong> {resendTicket?.code ?? 'Not found'}
                    </span>
                    {selectedResendQuote.emailsSent?.ticket && (
                      <span className="email-center-resend-meta__sent">
                        Last sent {new Date(selectedResendQuote.emailsSent.ticket).toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="email-center-btn email-center-btn--green"
                disabled={!resendQuoteId || !resendTicket || resending}
                onClick={() => void resendTicketEmail()}
              >
                {resending ? (
                  <>
                    <Loader2 size={18} className="email-center-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <RotateCcw size={18} /> Resend ticket
                  </>
                )}
              </button>
              {resendQuoteId && !resendTicket && (
                <p className="email-center-panel__warn">No linked support ticket for this quote — cannot resend from here.</p>
              )}
            </>
          )}
        </section>

        <section className="email-center-panel">
          <div className="email-center-panel__head">
            <div className="email-center-panel__icon-wrap">
              <Beaker size={22} />
            </div>
            <div>
              <h3>Test deliverability</h3>
              <p>Sends a real message via SMTP. From address comes from server <code>MAIL_FROM</code>.</p>
            </div>
          </div>
          <div className="email-center-panel__fields">
            <div className="email-center-field">
              <label htmlFor="test-to">Recipient</label>
              <input id="test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@company.com" />
            </div>
            <button
              type="button"
              className="email-center-btn email-center-btn--blue"
              disabled={sendingTest}
              onClick={() => void sendTest()}
            >
              {sendingTest ? <Loader2 size={18} className="email-center-spin" /> : <Send size={18} />}
              Send test
            </button>
          </div>
        </section>

        <section className="email-center-panel">
          <div className="email-center-panel__head">
            <div className="email-center-panel__icon-wrap email-center-panel__icon-wrap--amber">
              <DollarSign size={22} />
            </div>
            <div>
              <h3>Payment reminder</h3>
              <p>Sends the payment link and CMS reminder body for an unpaid quote.</p>
            </div>
          </div>
          {unpaidQuotes.length === 0 ? (
            <p className="email-center-panel__muted">No unpaid quotes in the system.</p>
          ) : (
            <div className="email-center-panel__fields">
              <div className="email-center-field">
                <label htmlFor="rem-quote">Unpaid quote</label>
                <select id="rem-quote" value={remQuoteId} onChange={(e) => setRemQuoteId(e.target.value)}>
                  <option value="">Select…</option>
                  {unpaidQuotes.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.clientSnapshot.name} · {q.flight.airline} {q.flight.flightNumber} · ${q.pricePerPerson}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="email-center-btn email-center-btn--primary"
                disabled={!remQuoteId || sendingReminder}
                onClick={() => void sendPaymentReminderFromCenter()}
              >
                {sendingReminder ? <Loader2 size={18} className="email-center-spin" /> : <Send size={18} />}
                Send reminder
              </button>
            </div>
          )}
        </section>
      </div>

      <section className="email-center-log">
        <div className="email-center-log__head">
          <Mail size={20} />
          <h3>Recent email log</h3>
          <span className="email-center-log__count">{filtered.length}</span>
        </div>
        {filtered.length === 0 ? (
          <p className="email-center-log__empty">No log entries match your search.</p>
        ) : (
          <ul className="email-center-log__list">
            {filtered.map((e) => {
              const vis = SECTION_VISUAL[e.kind];
              const delivery = resolveEmailLogDelivery(e);
              const StatusIcon =
                delivery.status === 'sent'
                  ? CheckCircle2
                  : delivery.status === 'failed'
                    ? XCircle
                    : delivery.status === 'partial'
                      ? AlertTriangle
                      : FileWarning;
              const previewBody = stripEmailLogPreviewBody(e.preview);
              return (
                <li key={e.id} className="email-center-log__item">
                  <div className="email-center-log__aside">
                    <span className="email-center-log__badge" style={{ background: vis.bg, color: vis.accent, borderColor: vis.border }}>
                      {KIND_LABEL[e.kind]}
                    </span>
                    <span className={`email-center-log__status email-center-log__status--${delivery.status}`}>
                      <StatusIcon size={14} aria-hidden />
                      {delivery.label}
                    </span>
                  </div>
                  <div className="email-center-log__main">
                    <div className="email-center-log__row">
                      <strong>{e.subject}</strong>
                      <time dateTime={e.at}>{new Date(e.at).toLocaleString()}</time>
                    </div>
                    <p className="email-center-log__route">
                      {e.agentEmail} → {e.to}
                    </p>
                    {delivery.shortError && delivery.status !== 'sent' && (
                      <p className="email-center-log__error" title={delivery.shortError}>
                        {delivery.shortError}
                      </p>
                    )}
                    <details className="email-center-log__details">
                      <summary>Message preview</summary>
                      <p className="email-center-log__preview">{previewBody}</p>
                    </details>
                    {delivery.canResend && (
                      <button
                        type="button"
                        className="email-center-log__resend"
                        disabled={resendingLogId === e.id}
                        onClick={() => void resendLogEntry(e)}
                      >
                        {resendingLogId === e.id ? (
                          <>
                            <Loader2 size={16} className="email-center-spin" /> Sending…
                          </>
                        ) : (
                          <>
                            <RotateCcw size={16} /> Resend
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default AdminEmailCenter;
